import {fromPromise} from "rxjs/internal-compatibility";
import {forkJoin, Observable, of} from "rxjs";
import {concatMap, map, reduce, switchMap, tap} from "rxjs/operators";
import {sortBy} from "lodash-es";
import { LocaleService } from 'internal-services/locale/locale.service';
import { IDiaryService, IEditScheduleService } from 'shared-types/DiaryTypes';
import PouchLoadService from "external-services/pouch-load-service";
import EventsLookupService from "external-services/events-lookup-service";

const NS = 'ServiceGroupsService'; // jshint ignore:line

interface IDoc {
  order?: number;
  ids: string[];
  isListTop?: boolean;
  name: string;
  _id?: string;
  _rev?: string;
}
interface IDocForPouch extends IDoc {
  _id: string;
  _rev: string;
}

interface IServiceGroupItem {
  doc: IDoc;
  id: string;
  key: string;
  value: {
    rev: string;
  };
}

interface IServiceGroups {
  offset: number;
  rows: IServiceGroupItem[];
  total_rows: number;
}


interface IEvent {
  name: string;
  date: Date;
  _id: string;
}
interface IScheduleDay {
  dayNum: number;
  isOpen: boolean;
  dayName: string;
  boookingInterval: number;
  services: IDiaryService[];
  order: number;
  vaildFrom: Date;
  _id: string;
  _rev: string;
}

class ServiceGroupsService{
  private scheduleService: any;
  private trackingService: any;
  private ID_MARKER: string = 'service-group';

  public setup(
    scheduleService: any,
    trackingService: any
  ): void {
    this.scheduleService = scheduleService;
    this.trackingService = trackingService;
  }

  public saveServiceGroup(serviceGroup: IDoc): Observable<PouchDB.Core.Response> {
    this.trackingService.trackEvent('Save Service Group');
    if (!serviceGroup._id) {
      serviceGroup._id = PouchLoadService.getNewId([this.ID_MARKER]);
    }
    return fromPromise(
      Promise.resolve(PouchLoadService.getDbLocal().put(serviceGroup))
    );
  }

  public saveServiceGroups(serviceGroups: IDoc[]): Observable<(PouchDB.Core.Response | PouchDB.Core.Error)[]> {
    this.trackingService.trackEvent('Save Service Groups');
    return fromPromise(
      Promise.resolve(PouchLoadService.getDbLocal().bulkDocs(serviceGroups))
    );
  }

  /**
   * Resolves to an Observable array of serviceGroup objects {name:{string}, order:{number}, ids:[array of strings]}
   */
  public getServiceGroups(): Observable<IDoc[]> {
    return fromPromise(
      Promise.resolve(PouchLoadService.getDbLocal().allDocs({
        include_docs: true,
        attachments: false,
        startkey: this.ID_MARKER + '_',
        endkey: this.ID_MARKER + '_\uffff'
      }).then((serviceGroups: unknown) => {
        return (serviceGroups as IServiceGroups).rows.map((item: IServiceGroupItem) => {
          return item.doc;
        });
      }))
    );
  }

  /**
   * Remove rxjs observe, use async/await, same work with getServiceGroups
   */
  public getServiceGroupsAsync(): Promise<IDoc[]> {
    return PouchLoadService.getDbLocal().allDocs({
      include_docs: true,
      attachments: false,
      startkey: this.ID_MARKER + '_',
      endkey: this.ID_MARKER + '_\uffff'
    }).then((serviceGroups: unknown) => {
      return (serviceGroups as IServiceGroups).rows.map((item: IServiceGroupItem) => {
        return item.doc;
      });
    });
  }

  /**
   * Scans through supplied services (not events) and checks for any services
   * that have the same name as an existing service group, then adds them if they match.
   * For example, a service group exists called 'Afternoon', which includes a 'Lunch'
   * service for Wednesday. A Thursday 'Lunch' service does not exist at this point, but
   * now gets added and we would expect the 'Afternoon' service group to include it, as
   * it has the same name as the Wednesday 'Lunch' service.
   * That's what this function does - looks for services in schedule that have same name
   * and adds them to a service group.
   */
  public updateScheduleServiceGroupsByName(newServices: IEditScheduleService[]): Observable<void> {

    const existingServiceGroups$ = this.getServiceGroups();
    const allServices$ = this.getServicesFromSchedule();

    return forkJoin(existingServiceGroups$, allServices$)
    .pipe(
      map(data => ({
        existingServiceGroups: data[0],
        allServices: data[1]
      })),
      switchMap(data => {
        const serviceWasAddedToName = this.addNewServicesWithMatchingNamesToServiceGroups(newServices, data.existingServiceGroups, data.allServices);
        const serviceWasRemovedFromSG = this.removeNonExistantServiceIds(data.existingServiceGroups, data.allServices);
        if (serviceWasAddedToName || serviceWasRemovedFromSG) {
          // saves the service groups with the updates
          return fromPromise(
            Promise.resolve(PouchLoadService.getDbLocal().bulkDocs(data.existingServiceGroups) as any)
          );
        } else {
          // if no names were changed to match a service group, do nothing
          return of(true);
        }
      })
    );
  }

  /**
   * Gets a list of objects with service data, taken from the schedule and events.
   * Resolves to an Observable array of objects {name:{service name}, ids:{array of service ids that match name}}
   */
  public getAllServices(): Observable<{
    events: {
      dateString: string;
      ids: string[];
      name: string;
    }[];
    services: {
      ids: string[];
      name: string;
    }[];
  }> {
    const events$ = this.getEvents();
    const services$ = this.getServicesFromSchedule();

    return forkJoin(
      events$, services$
    ).pipe(
      map(data => ({
        events: data[0],
        services: data[1]
      }))
    );
  }

  public deleteServiceGroup(sectionGroup: IDocForPouch): Promise<{
    id: string,// "service-group_1KNJJP6GKK7G7_1730174042409"
    ok: boolean
    rev: string //"24-5829e680e26348f992a8360048e92c57"
  }> {
    return Promise.resolve(PouchLoadService.getDbLocal().remove(sectionGroup));
  }

   /**
   * PRIVATE FUNCTIONS BELOW HERE
   */

  // gathers service objects from ids
  private getServiceObjectsFromIds(ids: string[], allServices: IDoc[]): IDoc[] {
    return ids.reduce((acc: IDoc[], id: any) => {
      const service: IDoc = allServices.find((s: IDoc) => s.ids.includes(id));
      if (service) {
        acc.push(service);
      }
      return acc;
    }, []);
  }

  /**
   * Takes an array of service objects that have been added (most likely when saving a day on the schedule) and compares
   * their name to existing services within existing service groups. If they match and have not been added yet, it adds them
   * to the `ids` array on the service group object.
   * @param {object[]} newServices - array of service objects that have been added (most likely when saving a day on the schedule)
   * @param {object[]} existingServiceGroups - service groups that already exist
   * @param {object[]} allServices - all services in the schedule, used for name matching
   * @return {boolean} true if updates were made, false if not
   */
   private addNewServicesWithMatchingNamesToServiceGroups(newServices: IEditScheduleService[], existingServiceGroups: IDoc[], allServices: IDoc[]): boolean {
    let updatesNeeded = false;
    existingServiceGroups.forEach((sg: IDoc) => {

      // gathers service objects from ids
      const servicesFromSGs: IDoc[] = this.getServiceObjectsFromIds(sg.ids, allServices);

      // compares provided services with the services grouped and matches them by name
      const servicesWithMatchingNames: IEditScheduleService[] = newServices.filter((ns: IEditScheduleService) => servicesFromSGs.find((s: IDoc) => ns.name === s.name));

      // filters out those that have matching name, but are not included in the service group's ids
      const newServicesToAdd: IEditScheduleService[] = servicesWithMatchingNames.filter((ns: IEditScheduleService) => !sg.ids.includes(ns._id));

      if (newServices.length) {
        updatesNeeded = true;
        // adds new service's id to service group's list of ids
        newServicesToAdd.forEach((ns: any) => {
          sg.ids.push(ns._id);
        });
      }
    });

    return updatesNeeded;
  }

  /**
   * Runs through service groups and cleans up any left over service ids that no longer exist.
   * @param {object[]} existingServiceGroups - service groups that already exist
   * @param {object[]} allServices - all services in the schedule, used for id matching
   * @return {boolean} true if updates were made, false if not
   */
   private removeNonExistantServiceIds(existingServiceGroups: IDoc[], allServices: IDoc[]): boolean {
    let updatesNeeded = false;
    existingServiceGroups.forEach((sg: IDoc) => {

      // loops over ids backwards so they can be spliced without affecting index order
      for (let i=sg.ids.length-1; i>=0; i--) {
        const id = sg.ids[i];
        const serviceIdStillExists = allServices.find((s: IDoc) => s.ids.includes(id));
        if (!serviceIdStillExists) {
          updatesNeeded = true;
          sg.ids.splice(i, 1);
        }
      }
    });

    return updatesNeeded;
  }

  /**
   * Gets a list of objects with service data, taken from the events.
   * Resolves to an Observable array of objects {name:{service name}, ids:{array of service ids that match name}}
   */
   private getEvents(): Observable<{
    dateString: string;
    ids: string[];
    name: string;
  }[]> {
    return fromPromise(EventsLookupService.getAll() as Promise<IEvent[]>)
    .pipe(
      map((events: IEvent[]) => events.map((e: IEvent) => {
        return {
          name: e.name,
          dateString: e.date.toLocaleDateString(LocaleService.getLocale()),
          ids: [e._id]
        };
      }))
    );
  }

  private getAllScheduleServices(): Observable<IDiaryService[]> {
    return fromPromise(this.scheduleService.getDaySchedules() as Promise<IScheduleDay[]>) // gets schedules as observable
    .pipe(
      concatMap((scheduleDays: IScheduleDay[]) => scheduleDays.map((day: IScheduleDay) => day.services)) // gathers all services from schedule
    );
  }

  /**
   * Gets a list of objects with service data, taken from the schedule.
   * Resolves to an Observable array of objects {name:{service name}, ids:{array of service ids that match name}}
   */
   private getServicesFromSchedule(): Observable<{
    ids: string[];
    name: string;
  }[]> {

    return this.getAllScheduleServices() // gathers all services from schedule
    .pipe(
      reduce((acc: { ids: string[]; name: string; }[], services: IDiaryService[]) => {
        // converts services into single array of objects {name:{service name}, ids:{array of service ids that match name}}
        services.forEach((s1: IDiaryService) => {
          const s2: IDoc = acc.find((s3: {name: string;}) => s3.name === s1.name);
          if (s2) {
            s2.ids.push(s1._id);
          } else {
            acc.push({
              name: s1.name,
              ids: [ s1._id ]
            });
          }
        });

        return acc;
      }, []),
      map((services: IDoc[]) => sortBy(services, 'name')) // sorts alphabetically
    );
  }
}

const instance = new ServiceGroupsService();
export default instance as ServiceGroupsService;
